/**
 * Admin / Front-Desk Escalation Queue
 *
 * Real-time ops dashboard for escalations filed by Bitsy's ReAct loop
 * when the AI hits a dead-end. Sorted by tier priority (platinum → gold →
 * silver → bronze → anonymous) then by age. Polls every 10s.
 *
 * Backend: /api/admin/escalations (already live)
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Clock, Check, X, AlertTriangle, Phone, Mail,
  MessageSquare, Users, Filter, ChevronDown, ChevronRight, Zap,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { API } from '@/lib/backendUrl';

const TIER_RANK = { platinum: 0, gold: 1, silver: 2, bronze: 3, anonymous: 4 };
const TIER_COLORS = {
  platinum: 'bg-slate-200 text-slate-900 border-slate-300',
  gold: 'bg-amber-400/25 text-amber-200 border-amber-400/40',
  silver: 'bg-zinc-400/20 text-zinc-200 border-zinc-400/30',
  bronze: 'bg-orange-700/20 text-orange-300 border-orange-700/40',
  anonymous: 'bg-zinc-700/30 text-zinc-400 border-zinc-700/50',
};
const URGENCY_COLORS = {
  high: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  medium: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  low: 'bg-zinc-600/15 text-zinc-300 border-zinc-600/30',
};
const CONTACT_ICON = { phone: Phone, email: Mail, in_app: MessageSquare };
const POLL_MS = 10000;

function fmtRelative(iso) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function etaRemaining(createdAtIso, etaMinutes) {
  if (!createdAtIso || !etaMinutes) return null;
  const due = new Date(createdAtIso).getTime() + etaMinutes * 60 * 1000;
  const left = due - Date.now();
  if (left <= 0) return { overdue: true, label: `${Math.floor(-left / 60000)}m overdue` };
  const m = Math.floor(left / 60000);
  return { overdue: false, label: `${m}m remaining` };
}

export default function AdminEscalationsPage() {
  const nav = useNavigate();
  const [status, setStatus] = useState('open');
  const [hotelId, setHotelId] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [contactFilter, setContactFilter] = useState('');
  const [items, setItems] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastLoaded, setLastLoaded] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [resolving, setResolving] = useState(null); // {id, notes}
  const [actionMsg, setActionMsg] = useState(null);
  const [resolverName, setResolverName] = useState(
    () => localStorage.getItem('escalation_resolver_name') || 'front-desk',
  );

  // ─── data load ───────────────────────────────────────────────
  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ status, limit: '200' });
      if (hotelId.trim()) params.set('hotel_id', hotelId.trim());
      const mparams = new URLSearchParams();
      if (hotelId.trim()) mparams.set('hotel_id', hotelId.trim());
      const [listRes, metricsRes] = await Promise.all([
        fetch(`${API}/api/admin/escalations?${params}`),
        fetch(`${API}/api/admin/escalations/metrics?${mparams}`),
      ]);
      const list = await listRes.json();
      const met = await metricsRes.json();
      setItems(list.escalations || []);
      setMetrics(met);
      setLastLoaded(Date.now());
    } catch {
      // keep prior state on transient failure
    }
    setLoading(false);
  }, [status, hotelId]);

  useEffect(() => { load(); }, [load]);

  // Poll while the tab is visible
  useEffect(() => {
    const iv = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, POLL_MS);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    localStorage.setItem('escalation_resolver_name', resolverName || '');
  }, [resolverName]);

  // ─── resolve action ─────────────────────────────────────────
  const resolve = async (id, notes) => {
    setActionMsg(null);
    try {
      const r = await fetch(`${API}/api/admin/escalations/${id}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resolved_by: resolverName || 'front-desk',
          resolution_notes: notes || '',
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'resolve failed');
      setActionMsg({ type: 'ok', text: `Resolved ${id}` });
      setResolving(null);
      await load();
    } catch (e) {
      setActionMsg({ type: 'err', text: e.message });
    }
  };

  // ─── filter + sort ──────────────────────────────────────────
  const visible = useMemo(() => {
    let rows = items;
    if (tierFilter) rows = rows.filter((r) => r.tier === tierFilter);
    if (contactFilter) rows = rows.filter((r) => r.preferred_contact === contactFilter);
    if (status === 'open') {
      rows = [...rows].sort(
        (a, b) =>
          (TIER_RANK[a.tier] ?? 99) - (TIER_RANK[b.tier] ?? 99) ||
          String(a.created_at).localeCompare(b.created_at),
      );
    }
    return rows;
  }, [items, tierFilter, contactFilter, status]);

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-zinc-100">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => nav('/admin')} data-testid="escalations-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin
            </Button>
            <div>
              <h1 className="text-2xl font-semibold flex items-center gap-2">
                <Zap className="w-6 h-6 text-amber-400" />
                Escalation Queue
              </h1>
              <p className="text-xs text-zinc-400">
                AI dead-ends routed to human staff · tier-aware SLA · live
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={resolverName}
              onChange={(e) => setResolverName(e.target.value)}
              className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-sm"
              placeholder="your name (appears in audit)"
              data-testid="escalations-resolver-name-input"
            />
            <Button variant="ghost" size="sm" onClick={load} data-testid="escalations-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Metrics strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <MetricCard testid="metric-open-total" label="Open" value={metrics?.open_total ?? '—'} tone="amber" />
          <MetricCard testid="metric-overdue" label="Overdue" value={metrics?.overdue_count ?? '—'} tone={metrics?.overdue_count ? 'rose' : 'zinc'} />
          <MetricCard testid="metric-resolved-today" label="Resolved today" value={metrics?.resolved_today ?? '—'} tone="emerald" />
          <MetricCard testid="metric-median-mins" label="Median resolution" value={metrics?.median_resolution_minutes_last_50 ? `${metrics.median_resolution_minutes_last_50}m` : '—'} tone="blue" />
          <div className="col-span-2 md:col-span-1">
            <Card className="bg-zinc-900/60 border-zinc-800 h-full">
              <CardContent className="p-3">
                <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">By tier (open)</div>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(metrics?.open_by_tier || {}).map(([t, n]) => (
                    <span
                      key={t}
                      className={`text-[11px] px-1.5 py-0.5 rounded border ${TIER_COLORS[t] || ''}`}
                      data-testid={`metric-tier-${t}`}
                    >
                      {t} {n}
                    </span>
                  ))}
                  {!metrics?.open_by_tier && <span className="text-[11px] text-zinc-500">—</span>}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap mb-4">
          {['open', 'resolved', 'all'].map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-full text-sm border transition ${
                status === s
                  ? 'bg-amber-500/20 border-amber-500/50 text-amber-200'
                  : 'border-zinc-800 text-zinc-400 hover:bg-zinc-900'
              }`}
              data-testid={`escalations-status-filter-${s}-btn`}
            >
              {s}
            </button>
          ))}
          <div className="mx-2 h-5 w-px bg-zinc-800" />
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm"
            data-testid="escalations-tier-filter-select"
          >
            <option value="">all tiers</option>
            {['platinum', 'gold', 'silver', 'bronze', 'anonymous'].map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <select
            value={contactFilter}
            onChange={(e) => setContactFilter(e.target.value)}
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm"
            data-testid="escalations-contact-filter-select"
          >
            <option value="">any contact</option>
            {['phone', 'email', 'in_app'].map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <input
            type="text"
            value={hotelId}
            onChange={(e) => setHotelId(e.target.value)}
            placeholder="filter by hotel_id"
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm"
            data-testid="escalations-hotel-filter-input"
          />
          {lastLoaded && (
            <span className="text-[11px] text-zinc-500 ml-auto">
              polling · refreshed {fmtRelative(new Date(lastLoaded).toISOString())}
            </span>
          )}
        </div>

        {actionMsg && (
          <div
            className={`mb-3 px-3 py-2 rounded text-sm ${
              actionMsg.type === 'ok'
                ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
                : 'bg-rose-500/10 text-rose-300 border border-rose-500/30'
            }`}
            data-testid="escalations-action-msg"
          >
            {actionMsg.text}
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="text-zinc-500 py-12 text-center">Loading…</div>
        ) : visible.length === 0 ? (
          <Card className="bg-zinc-900/40 border-zinc-800">
            <CardContent className="py-10 text-center text-zinc-500">
              Nothing matches these filters. Quiet shift — or tighten the search.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {visible.map((esc) => (
              <EscalationRow
                key={esc.id}
                esc={esc}
                open={openId === esc.id}
                onToggle={() => setOpenId(openId === esc.id ? null : esc.id)}
                onResolve={() => setResolving({ id: esc.id, notes: '' })}
              />
            ))}
          </div>
        )}

        {/* Resolve modal */}
        {resolving && (
          <ResolveModal
            resolving={resolving}
            setResolving={setResolving}
            onSubmit={() => resolve(resolving.id, resolving.notes)}
          />
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value, tone, testid }) {
  const tones = {
    amber: 'text-amber-300',
    rose: 'text-rose-300',
    emerald: 'text-emerald-300',
    blue: 'text-blue-300',
    zinc: 'text-zinc-300',
  };
  return (
    <Card className="bg-zinc-900/60 border-zinc-800" data-testid={testid}>
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
        <div className={`text-2xl font-semibold ${tones[tone] || 'text-zinc-100'}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function EscalationRow({ esc, open, onToggle, onResolve }) {
  const eta = etaRemaining(esc.created_at, esc.eta_minutes);
  const overdue = eta?.overdue;
  const ContactIcon = CONTACT_ICON[esc.preferred_contact] || MessageSquare;
  const isResolved = esc.status === 'resolved';

  // Derive a tel:/mailto: target if guest_id looks like a phone/email
  const guest = esc.guest_id || '';
  const telTarget =
    esc.preferred_contact === 'phone' && /^\+?[\d\s\-().]{6,}$/.test(guest) ? `tel:${guest.replace(/\s+/g, '')}` : null;
  const mailTarget =
    esc.preferred_contact === 'email' && /@/.test(guest) ? `mailto:${guest}` : null;

  return (
    <Card
      className={`bg-zinc-900/60 border-zinc-800 ${overdue && !isResolved ? 'ring-1 ring-rose-500/40' : ''}`}
      data-testid={`escalation-row-${esc.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <button
            onClick={onToggle}
            className="text-zinc-500 hover:text-zinc-200 mt-1"
            data-testid={`escalation-toggle-${esc.id}-btn`}
          >
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <Badge className={TIER_COLORS[esc.tier] || ''} data-testid={`escalation-tier-${esc.id}`}>
                {esc.tier}
              </Badge>
              <Badge className={URGENCY_COLORS[esc.urgency] || ''}>{esc.urgency}</Badge>
              <Badge className="bg-zinc-800 text-zinc-300 border-zinc-700">
                <ContactIcon className="w-3 h-3 mr-1" />
                {esc.preferred_contact}
              </Badge>
              {esc.high_tier_auto_escalation && (
                <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30">auto-escalated</Badge>
              )}
              {isResolved && (
                <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
                  resolved by {esc.resolved_by}
                </Badge>
              )}
            </div>
            <div className="text-sm text-zinc-200 line-clamp-2">
              <span className="font-medium">{esc.reason || '(no reason)'}</span>
            </div>
            <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
              <span>id {esc.id}</span>
              {esc.hotel_id && <span>· {esc.hotel_id}</span>}
              <span>· {fmtRelative(esc.created_at)}</span>
              {esc.score ? <span>· score {esc.score}</span> : null}
              {esc.total_stays ? <span>· {esc.total_stays} stays</span> : null}
              {!isResolved && eta && (
                <span className={overdue ? 'text-rose-300' : 'text-blue-300'}>
                  <Clock className="inline w-3 h-3 mr-0.5" /> {eta.label}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            {!isResolved && (
              <div className="flex items-center gap-1.5">
                {telTarget && (
                  <a
                    href={telTarget}
                    className="inline-flex items-center text-xs px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-white"
                    data-testid={`escalation-call-${esc.id}-btn`}
                  >
                    <Phone className="w-3.5 h-3.5 mr-1" /> Call
                  </a>
                )}
                {mailTarget && (
                  <a
                    href={mailTarget}
                    className="inline-flex items-center text-xs px-2 py-1 rounded bg-cyan-600 hover:bg-cyan-500 text-white"
                    data-testid={`escalation-email-${esc.id}-btn`}
                  >
                    <Mail className="w-3.5 h-3.5 mr-1" /> Email
                  </a>
                )}
                <Button
                  size="sm"
                  onClick={onResolve}
                  className="bg-amber-600 hover:bg-amber-500"
                  data-testid={`escalation-resolve-${esc.id}-btn`}
                >
                  <Check className="w-4 h-4 mr-1" /> Resolve
                </Button>
              </div>
            )}
          </div>
        </div>

        {open && (
          <div className="mt-3 pt-3 border-t border-zinc-800/80 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div>
              <div className="text-zinc-500 mb-1 flex items-center gap-1">
                <Users className="w-3 h-3" /> Guest summary (for Bitsy handoff)
              </div>
              <div className="bg-black/40 p-2 rounded text-zinc-200 whitespace-pre-wrap">
                {esc.summary || '(no summary)'}
              </div>
              {esc.session_id && (
                <div className="text-zinc-500 mt-2">session · <span className="font-mono text-zinc-300">{esc.session_id}</span></div>
              )}
              {esc.trusted_handle && (
                <div className="text-zinc-500 mt-1">trusted handle · <span className="font-mono text-emerald-300">{esc.trusted_handle}</span></div>
              )}
              {esc.wallet && (
                <div className="text-zinc-500 mt-1">wallet · <span className="font-mono text-zinc-300">{esc.wallet.slice(0, 10)}…{esc.wallet.slice(-6)}</span></div>
              )}
            </div>
            <div>
              <div className="text-zinc-500 mb-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Bitsy trajectory (why it dead-ended)
              </div>
              {(esc.trajectory_summary && esc.trajectory_summary.length > 0) ? (
                <ol className="bg-black/40 p-2 rounded text-zinc-300 space-y-0.5 list-decimal list-inside">
                  {esc.trajectory_summary.map((t, i) => (
                    <li key={i} className="font-mono">{t}</li>
                  ))}
                </ol>
              ) : (
                <div className="text-zinc-600 italic">no tool trajectory recorded</div>
              )}
              {esc.resolution_notes && (
                <div className="mt-2">
                  <div className="text-zinc-500 mb-1">Resolution notes</div>
                  <div className="bg-emerald-500/5 border border-emerald-500/20 p-2 rounded text-zinc-200">
                    {esc.resolution_notes}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ResolveModal({ resolving, setResolving, onSubmit }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4"
      onClick={(e) => { if (e.target === e.currentTarget) setResolving(null); }}
      data-testid="escalations-resolve-modal"
    >
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 w-full max-w-lg">
        <div className="flex items-center justify-between mb-3">
          <div className="text-lg font-semibold">Resolve {resolving.id}</div>
          <Button variant="ghost" size="sm" onClick={() => setResolving(null)} data-testid="escalations-resolve-modal-close-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="text-xs text-zinc-400 mb-2">
          Notes will be saved on the escalation record and visible to ops reviewers.
        </div>
        <textarea
          autoFocus
          value={resolving.notes}
          onChange={(e) => setResolving({ ...resolving, notes: e.target.value })}
          placeholder="e.g. called guest, confirmed accessible room al_signature, held until check-in."
          className="w-full bg-black/50 border border-zinc-800 rounded p-2 text-sm text-zinc-100 min-h-[100px]"
          data-testid="escalations-resolve-notes-input"
        />
        <div className="flex items-center justify-end gap-2 mt-3">
          <Button variant="outline" size="sm" onClick={() => setResolving(null)} data-testid="escalations-resolve-cancel-btn">
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={onSubmit}
            className="bg-emerald-600 hover:bg-emerald-500"
            disabled={!resolving.notes?.trim()}
            data-testid="escalations-resolve-submit-btn"
          >
            <Check className="w-4 h-4 mr-1" /> Mark resolved
          </Button>
        </div>
      </div>
    </div>
  );
}
